/**
 * Client-Side Image Compression & Normalization Utility
 * Standardizes images before upload to reduce bandwidth, strip EXIF/PII metadata,
 * and normalize orientation/resolution for AI inference and GCS archiving.
 */

const toJpegFile = (canvas, fileName, quality) =>
  new Promise((resolve, reject) => {
    const cleanFileName = (fileName || "capture.jpg").replace(/\.[^/.]+$/, "") + ".jpg";
    if (typeof canvas.toBlob === "function") {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to compress image"));
            return;
          }
          resolve(
            new File([blob], cleanFileName, {
              type: "image/jpeg",
              lastModified: Date.now(),
            })
          );
        },
        "image/jpeg",
        quality
      );
    } else if (typeof canvas.toDataURL === "function") {
      try {
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        const base64Data = dataUrl.split(",")[1];
        const byteString = atob(base64Data);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: "image/jpeg" });
        resolve(
          new File([blob], cleanFileName, {
            type: "image/jpeg",
            lastModified: Date.now(),
          })
        );
      } catch (err) {
        reject(err);
      }
    } else {
      reject(new Error("Canvas blob conversion unsupported"));
    }
  });

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    if (typeof FileReader === "undefined") {
      reject(new Error("FileReader API is not supported in this environment"));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () =>
        reject(
          new Error(
            "Format foto tidak didukung. Gunakan JPG/PNG atau ambil foto ulang."
          )
        );
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

/**
 * Calculate file or blob size in Megabytes (MB)
 * @param {File|Blob} file
 * @returns {number} Size in MB
 */
export const getFileSizeMB = (file) => {
  if (!file || typeof file.size !== "number") return 0;
  return file.size / (1024 * 1024);
};

/**
 * Compress image for upload while preserving enough detail for detection.
 * Standardizes output to image/jpeg, strips EXIF metadata, and resizes to bounds.
 * Iteratively reduces dimensions and quality if needed to stay below maxSizeMB.
 *
 * @param {File|Blob} file - Source image file or blob
 * @param {Object} [options={}] - Compression options
 * @param {number} [options.maxWidth=1600] - Maximum width in pixels
 * @param {number} [options.maxHeight=1600] - Maximum height in pixels
 * @param {number} [options.quality=0.85] - Initial JPEG quality (0.0 to 1.0)
 * @param {number} [options.maxSizeMB=1.5] - Target maximum file size in MB
 * @param {number} [options.minQuality=0.45] - Lowest quality to try
 * @param {boolean} [options.fallbackToOriginal=true] - Return original file if compression fails
 * @returns {Promise<File|Blob>}
 */
export const compressImage = async (file, options = {}) => {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.85,
    maxSizeMB = 1.5,
    minQuality = 0.45,
    fallbackToOriginal = true,
  } = options;

  if (!file) {
    if (fallbackToOriginal) return file;
    throw new Error("No image file provided for compression");
  }

  // Validate MIME type if file has a type
  if (file.type && !file.type.startsWith("image/")) {
    if (fallbackToOriginal) return file;
    throw new Error("Provided file is not a supported image type");
  }

  try {
    const maxBytes = maxSizeMB * 1024 * 1024;
    const img = await loadImage(file);

    const naturalWidth = img.naturalWidth || img.width;
    const naturalHeight = img.naturalHeight || img.height;

    if (!naturalWidth || !naturalHeight) {
      if (fallbackToOriginal) return file;
      throw new Error("Invalid image dimensions");
    }

    const baseScale = Math.min(
      1,
      maxWidth / naturalWidth,
      maxHeight / naturalHeight
    );

    let dimensionFactor = 1;
    while (dimensionFactor >= 0.55) {
      const width = Math.max(1, Math.round(naturalWidth * baseScale * dimensionFactor));
      const height = Math.max(
        1,
        Math.round(naturalHeight * baseScale * dimensionFactor)
      );

      if (typeof document === "undefined" || !document.createElement) {
        if (fallbackToOriginal) return file;
        throw new Error("DOM document is not available for canvas operations");
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        if (fallbackToOriginal) return file;
        throw new Error("Canvas 2D context is not available");
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, width, height);

      let currentQuality = quality;
      while (currentQuality >= minQuality) {
        const compressed = await toJpegFile(canvas, file.name, currentQuality);
        if (compressed.size <= maxBytes) {
          return compressed;
        }
        currentQuality -= 0.08;
      }

      dimensionFactor *= 0.85;
    }

    // Last resort: smallest dimensions at minimum quality
    const width = Math.max(1, Math.round(naturalWidth * baseScale * 0.55));
    const height = Math.max(1, Math.round(naturalHeight * baseScale * 0.55));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, width, height);
      return await toJpegFile(canvas, file.name, minQuality);
    }

    if (fallbackToOriginal) return file;
    throw new Error("Canvas context creation failed");
  } catch (err) {
    if (fallbackToOriginal) {
      return file;
    }
    throw err;
  }
};

/**
 * Alias for standardizeImage to match survey recommendations
 */
export const standardizeImage = compressImage;

export default compressImage;
